import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  LeftOutlined,
  LockOutlined,
  RightOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Flex,
  Grid,
  Input,
  InputNumber,
  Progress,
  Row,
  Segmented,
  Skeleton,
  Space,
  Tag,
  theme,
  Tree,
  Typography,
} from 'antd';
import type { TreeDataNode } from 'antd';
import { AcentoThemeProvider, libraryAccent, useEstadoDeLista } from '@titularis/ui';
import {
  AREAS_DA_LEI,
  AVISO_DO_TETO_DIARIO,
  chaveDoBloqueio,
  ehTetoDiario,
  motivoDoBloqueio,
  nomeDaArea,
  type AchadoNoTextoDaLei,
  type ArtigoParaLer,
  type DivisaoDaLei,
  type LeiDoCatalogo,
  type MotivoDeBloqueio,
  type RecorteDaLeitura,
} from '@titularis/api-client';
import { registrarEvento, registrarTela } from '../analytics';
import { api } from '../api';
import { useSession } from '../auth';
import { CabecalhoDaTela } from '../components/CabecalhoDaTela';
import { ConteudoRico } from '../components/ConteudoRico';
import { useBloqueio } from '../components/Oferta';
import { comoSeCitaOArtigo, contar, emMilhar } from '../texto';
import { arvoreDeSecoes, paginaPedida, type NoDaArvore } from './legislacao/navegacao';
import { guardarLeitura, lerLeituraRecente } from './legislacao/leitura-recente';
import { MEDIDA_DE_LEITURA, semAnotacao } from './legislacao/leitura';

/**
 * Legislação Comentada (F14).
 *
 * **A lei inteira, com alguns artigos comentados.** O texto oficial sai para
 * qualquer conta - é domínio público, está no Planalto - e o comentário autoral
 * é o que se vende (D-42). Por isso o artigo sem direito aparece com o cadeado e
 * não some: o aluno precisa ver que há algo ali para querer assiná-lo.
 *
 * **Nada de PDF, nada de imagem.** A geração anterior entregava página
 * rasterizada com marca d'água composta no servidor (D-19); rasterizar
 * comentário custaria a leitura no celular e a busca por uma proteção que aquele
 * mesmo lock já declarava não ser prevenção. O que fica é a metade que funciona:
 * a identificação declarada, no rodapé da leitura.
 */
const AVISO =
  'Os comentários deste material são conteúdo autoral, preparado para o seu estudo - por isso não podem ser compartilhados, publicados ou revendidos. A sua leitura é identificada.';

/** Artigos por página. O mesmo do backend, que é quem realmente decide. */
const POR_PAGINA = 25;

export function LegislacaoPage() {
  const { ler } = useEstadoDeLista();
  const bloquear = useBloqueio();

  /*
   * O denominador do módulo. Uma tela só para o catálogo e para o leitor, de
   * propósito: o que distingue os dois é o `article_opened`, que diz **qual**
   * comentário foi lido - e é a pergunta melhor.
   */
  useEffect(() => registrarTela('/legislacao'), []);

  const leiId = ler.texto('lei');
  /*
    Aqui o bloqueio é sempre de comentário: o texto da lei é aberto a qualquer
    conta autenticada (D-42), e o que se cobra é o comentário.

    **A chave vem do 403**, e não escrita aqui. Era `commented_laws` fixa, que
    abria o acervo inteiro; desde a produtização cada lei tem a sua (D-44), e é
    ela que faz o modal oferecer o produto certo - a tela não tem como saber
    qual lei trancou.
  */
  const aoBloquear = useCallback(
    (motivo: MotivoDeBloqueio, chave: string | null) =>
      bloquear({ motivo, origem: 'commentary', chave }),
    [bloquear],
  );

  return (
    <AcentoThemeProvider acento={libraryAccent}>
      {leiId ? <Leitor leiId={leiId} onBloqueio={aoBloquear} /> : <Catalogo />}
    </AcentoThemeProvider>
  );
}

/* ----------------------------------------------------------- catálogo */

/**
 * O acervo: todas as leis publicadas, com o que já está comentado.
 *
 * **Era uma grade e mais nada.** Quinze cartões em ordem fixa, sem busca e sem
 * recorte - e como só duas leis têm comentário hoje, treze deles diziam
 * "Comentários a caminho" embaixo de uma barra vazia. A tela do módulo que se
 * chama *Comentada* mostrava, na maior parte da sua área, o que ainda não foi
 * comentado.
 *
 * O que mudou:
 *
 * - **busca por nome ou citação**, que é como se procura uma lei ("registros
 *   públicos", "6.015", "CNN");
 * - **um recorte** entre todas e as que já têm comentário - o filtro que
 *   responde "o que eu ganho aqui hoje";
 * - **duas formas de ver**: a grade, que é vitrine, e a lista, que é índice -
 *   quem sabe o nome da lei acha mais rápido numa lista de quinze linhas do que
 *   numa parede de cartões;
 * - **as comentadas primeiro**, sempre: é o que o módulo tem de melhor.
 *
 * A etiqueta "grátis" saiu de todos os cartões. Ela estava em onze das quinze
 * leis - era o estado normal do acervo, dito em verde quinze vezes. O que
 * informa é a exceção: a lei cujo comentário está trancado continua com o
 * cadeado.
 */
function Catalogo() {
  const { ler, filtrar, irParaPagina } = useEstadoDeLista();
  const telas = Grid.useBreakpoint();

  const catalogo = useQuery({ queryKey: ['laws'], queryFn: () => api.listLaws() });

  const busca = ler.texto('busca') ?? '';
  const soComentadas = ler.texto('recorte') === 'comentadas';
  /*
   * A forma de ver é preferência de quem lê, e não endereço: ela não muda o
   * que a tela mostra, só como. Fica no `localStorage`, como o recolhimento da
   * barra lateral - e no celular a grade de uma coluna já é uma lista, então
   * lá o seletor nem aparece.
   */
  const [forma, setForma] = useState<FormaDeVer>(
    () => (globalThis.localStorage?.getItem(CHAVE_DA_FORMA) as FormaDeVer | null) ?? 'grade',
  );

  const escolherForma = (proxima: FormaDeVer) => {
    setForma(proxima);
    globalThis.localStorage?.setItem(CHAVE_DA_FORMA, proxima);
  };

  const leis = catalogo.data ?? [];
  const comentadas = leis.filter((lei) => lei.comentados > 0).length;

  const visiveis = leis
    .filter((lei) => (soComentadas ? lei.comentados > 0 : true))
    .filter((lei) => casaComABusca(lei, busca))
    /*
     * As comentadas primeiro, e o resto na ordem que o servidor mandou. É o
     * único critério que a tela impõe, e ele diz o que o módulo entrega hoje.
     */
    .sort((a, b) => Number(b.comentados > 0) - Number(a.comentados > 0));

  // `telas.md` é indefinido antes do primeiro cálculo do breakpoint, e a
  // prateleira quer um booleano de verdade.
  const emLista = forma === 'lista' && telas.md === true;

  /*
   * **A estante só existe quando se está navegando.** Agrupar é para quem
   * chegou sem saber o nome da lei: o catálogo vira prateleira e a pessoa
   * percorre o que interessa. Quem digitou "6.015" ou pediu só as comentadas
   * fez uma pergunta fechada, e espalhar três resultados por três títulos de
   * seção seria transformar a resposta em busca de novo.
   */
  const agrupado = !busca.trim() && !soComentadas;
  const estantes = agrupado ? porEstante(visiveis) : [];

  /*
   * O termo da busca no texto vai **atrasado**: a do catálogo é local e
   * instantânea, mas esta é uma ida ao servidor, e uma por tecla seria uma
   * varredura do acervo por letra digitada.
   */
  const [termoNoTexto, setTermoNoTexto] = useState(busca);
  useEffect(() => {
    const relogio = setTimeout(() => setTermoNoTexto(busca), 350);
    return () => clearTimeout(relogio);
  }, [busca]);

  const noTexto = useQuery({
    queryKey: ['law-search', termoNoTexto],
    queryFn: () => api.searchLaws(termoNoTexto),
    enabled: termoNoTexto.trim().length >= 3,
    /* O acervo muda por importação, não por minuto: um minuto de validade
       poupa a varredura quando a pessoa apaga uma letra e digita de novo. */
    staleTime: 60_000,
  });

  /*
   * A volta para onde parou. Lida no render, e não em estado: o valor vem do
   * `localStorage`, que não muda enquanto esta tela está aberta - quem escreve
   * é o leitor, e voltar dele remonta o catálogo.
   */
  const recente = lerLeituraRecente();
  const leiRecente = recente ? leis.find((lei) => lei.id === recente.leiId) : undefined;

  /*
   * O achado abre a lei **no artigo**, e quem sabe em que página ele cai é o
   * servidor: a tela manda o artigo na URL e o leitor faz o salto ao montar -
   * é o mesmo caminho do campo "Ir para o artigo", que já existia lá dentro.
   */
  const irParaArtigoDaBusca = (achado: AchadoNoTextoDaLei) =>
    filtrar({ lei: achado.legislationId, artigo: achado.article, page: undefined });

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <CabecalhoDaTela
        titulo="Legislação Comentada"
        resumo="O texto oficial de cada lei, com o comentário autoral nos artigos que a prova cobra."
        acento={libraryAccent}
        direita={
          leis.length > 0 ? (
            <Flex vertical align={telas.md ? 'flex-end' : 'flex-start'} gap={2}>
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                No acervo
              </Typography.Text>
              <Typography.Text strong>{contar(leis.length, 'lei', 'leis')}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {comentadas > 0
                  ? contar(comentadas, 'já comentada', 'já comentadas')
                  : 'comentários a caminho'}
              </Typography.Text>
            </Flex>
          ) : null
        }
      />

      {catalogo.error ? (
        <Alert type="error" showIcon title={(catalogo.error as Error).message} />
      ) : null}

      {catalogo.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : leis.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Ainda não há legislação comentada publicada."
        />
      ) : (
        <>
          {/*
            **A volta para onde parou vem antes de tudo.** Quem lê uma lei de
            dois mil artigos não termina numa sentada, e sem esta linha voltava
            para o catálogo inteiro tendo de lembrar qual lei era e em que
            página tinha ficado.
          */}
          {leiRecente && recente ? (
            <ContinuarLendo
              titulo={leiRecente.title}
              citacao={leiRecente.citation}
              pagina={recente.pagina}
              onAbrir={() => irParaPagina(recente.pagina, { lei: leiRecente.id })}
            />
          ) : null}

          <Flex align="center" justify="space-between" gap={12} wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar por nome ou número da lei"
              defaultValue={busca}
              onChange={(evento) => filtrar({ busca: evento.target.value || undefined })}
              style={{ maxWidth: 340 }}
            />

            <Flex align="center" gap={8} wrap>
              <Segmented
                value={soComentadas ? 'comentadas' : 'todas'}
                onChange={(valor) =>
                  filtrar({ recorte: valor === 'comentadas' ? 'comentadas' : undefined })
                }
                options={[
                  { value: 'todas', label: 'Todas' },
                  { value: 'comentadas', label: 'Comentadas' },
                ]}
              />
              {telas.md ? (
                <Segmented
                  value={forma}
                  onChange={(valor) => escolherForma(valor as FormaDeVer)}
                  options={[
                    { value: 'grade', icon: <AppstoreOutlined />, tooltip: 'Ver em grade' },
                    { value: 'lista', icon: <UnorderedListOutlined />, tooltip: 'Ver em lista' },
                  ]}
                />
              ) : null}
            </Flex>
          </Flex>

          {visiveis.length === 0 && busca ? (
            /*
              Buscando, o vazio do catálogo é **uma linha** e não a ilustração:
              logo abaixo vêm os achados no texto das leis, e uma caixa vazia
              do tamanho de um cartão entre a busca e eles diria "não achei"
              em cima de uma lista de coisas achadas.
            */
            <Typography.Text type="secondary">
              Nenhuma lei com “{busca.trim()}” no nome.
            </Typography.Text>
          ) : visiveis.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhuma lei comentada ainda."
            />
          ) : agrupado ? (
            <Flex vertical gap={emLista ? 20 : 24}>
              {estantes.map(({ nome, leis: daEstante }) => (
                <Flex key={nome} vertical gap={emLista ? 4 : 12}>
                  <TituloDaEstante nome={nome} quantas={daEstante.length} />
                  <Prateleira
                    leis={daEstante}
                    emLista={emLista}
                    onAbrir={(lei) => filtrar({ lei: lei.id, page: undefined })}
                  />
                </Flex>
              ))}
            </Flex>
          ) : (
            <Prateleira
              leis={visiveis}
              emLista={emLista}
              onAbrir={(lei) => filtrar({ lei: lei.id, page: undefined })}
            />
          )}

          {/*
            **A busca dentro do texto responde outra pergunta.** A de cima
            procura a lei pelo nome; esta procura o assunto dentro dela - que é
            o que o aluno tem na cabeça quando escreve "usucapião". Vem depois
            do catálogo porque é aprofundamento, e não substituto: quem digitou
            "civil" quer o Código Civil primeiro.
          */}
          <BuscaNoTexto
            termo={termoNoTexto}
            achados={noTexto.data}
            carregando={noTexto.isFetching}
            onAbrir={(achado) => irParaArtigoDaBusca(achado)}
          />
        </>
      )}
    </Space>
  );
}

type FormaDeVer = 'grade' | 'lista';

const CHAVE_DA_FORMA = 'titularis:legislacao-forma';

/** Busca por nome ou por número: "registros públicos", "6.015", "CNN". */
function casaComABusca(lei: LeiDoCatalogo, busca: string): boolean {
  const termo = busca.trim().toLowerCase();
  if (!termo) return true;
  return `${lei.title} ${lei.citation}`.toLowerCase().includes(termo);
}

/** O quanto da lei já tem comentário, em uma frase - ou nada, se for zero. */
function cobertura(lei: LeiDoCatalogo): string | null {
  if (lei.comentados === 0) return null;
  return `${emMilhar(lei.comentados)} de ${emMilhar(lei.artigos)} comentados`;
}

/**
 * A etiqueta do acesso.
 *
 * **Só a exceção fala.** "Grátis" estava em onze das quinze leis - era o estado
 * normal do acervo, repetido em verde em quase todo cartão, competindo com o
 * número da lei ao lado. O que o aluno precisa ver é o que está trancado.
 */
function EtiquetaDoAcesso({ lei }: { lei: LeiDoCatalogo }) {
  if (lei.acesso !== 'bloqueada') return null;

  return (
    <Tag icon={<LockOutlined />} style={{ marginInlineEnd: 0 }}>
      comentários bloqueados
    </Tag>
  );
}

function CartaoDaLei({ lei, onAbrir }: { lei: LeiDoCatalogo; onAbrir: () => void }) {
  const { token } = theme.useToken();
  const quanto = cobertura(lei);

  return (
    <Card
      hoverable
      size="small"
      style={{ height: '100%' }}
      onClick={onAbrir}
      styles={{ body: { height: '100%' } }}
    >
      {/*
        `Flex` e não `Space`: o `Space` embrulha cada filho num
        `.ant-space-item`, e o `marginTop: auto` do rodapé ficava no `div` de
        dentro em vez de no item do flex - as barras da mesma fileira
        continuavam em alturas diferentes, que é exatamente o defeito que este
        bloco veio consertar.
      */}
      <Flex vertical gap={8} style={{ width: '100%', height: '100%' }}>
        <Flex align="center" gap={6} wrap>
          <Tag color={libraryAccent} style={{ marginInlineEnd: 0 }}>
            {lei.citation}
          </Tag>
          <EtiquetaDoAcesso lei={lei} />
        </Flex>

        <Typography.Text strong>{lei.title}</Typography.Text>

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {contar(lei.artigos, 'artigo', 'artigos')}
        </Typography.Text>

        {/*
          **A barra só aparece quando há o que mostrar.** Ela mede cobertura
          editorial e era lida como progresso de estudo; vazia, em treze dos
          quinze cartões, dizia "você não estudou nada disto" treze vezes. Onde
          há comentário ela fica - ali o avanço da casa é notícia boa.

          `marginTop: auto` prende o rodapé embaixo: sem ele, título de duas
          linhas empurrava a barra e as da mesma fileira ficavam em alturas
          diferentes.
        */}
        <div style={{ marginTop: 'auto', width: '100%' }}>
          {quanto ? (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {quanto}
              </Typography.Text>
              <Progress
                percent={lei.artigos > 0 ? Math.round((lei.comentados / lei.artigos) * 100) : 0}
                size="small"
                showInfo={false}
                strokeColor={libraryAccent}
              />
            </>
          ) : (
            /*
              Sem a barra, mas com a frase: os cartões da mesma fileira crescem
              juntos, e um pé em branco embaixo do nome da lei lê como cartão
              quebrado. A cor é a mais apagada da escala - é uma nota de rodapé,
              não um aviso.
            */
            <Typography.Text style={{ fontSize: 12, color: token.colorTextQuaternary }}>
              comentários a caminho
            </Typography.Text>
          )}
        </div>
      </Flex>
    </Card>
  );
}

/**
 * A lei em uma linha, para quem já sabe o que procura.
 *
 * Quinze cartões numa parede pedem varredura; quinze linhas com o nome à
 * esquerda e a cobertura à direita pedem só um olhar. O fio separa uma da
 * outra, e o alvo do clique é a linha inteira.
 */
function LinhaDaLei({
  lei,
  primeira,
  onAbrir,
}: {
  lei: LeiDoCatalogo;
  primeira: boolean;
  onAbrir: () => void;
}) {
  const { token } = theme.useToken();
  const quanto = cobertura(lei);

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={16}
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          onAbrir();
        }
      }}
      style={{
        paddingBlock: 12,
        borderTop: primeira ? undefined : `1px solid ${token.colorSplit}`,
        cursor: 'pointer',
      }}
    >
      <Flex vertical gap={2} style={{ minWidth: 0 }}>
        <Flex align="center" gap={8} wrap>
          <Typography.Text strong ellipsis>
            {lei.title}
          </Typography.Text>
          <EtiquetaDoAcesso lei={lei} />
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {lei.citation} · {contar(lei.artigos, 'artigo', 'artigos')}
        </Typography.Text>
      </Flex>

      <Typography.Text
        type="secondary"
        style={{
          fontSize: 12,
          textAlign: 'end',
          flexShrink: 0,
          color: quanto ? undefined : token.colorTextQuaternary,
        }}
      >
        {quanto ?? 'a caminho'}
      </Typography.Text>
    </Flex>
  );
}

/**
 * As leis arrumadas em estantes, na ordem da estante.
 *
 * **A prateleira vazia não aparece.** Renderizar as sete sempre encheria o
 * catálogo de títulos com nada embaixo, e a estante existe para reduzir o que se
 * percorre, não para aumentá-lo.
 *
 * **A mesma lei aparece em mais de uma prateleira**, e é o ponto: a Lei de
 * Execuções Fiscais é processual e tributária, e quem varre a estante de
 * Tributário tem de achá-la ali. O total das prateleiras passa a ser maior do
 * que o número de leis, e por isso a contagem de cada uma diz "leis" e não uma
 * fração do acervo.
 *
 * As leis sem estante nenhuma caem em "Outras", **no fim**. Vazia é estado
 * legítimo do acervo, e "Outras" no começo poria o que ninguém classificou na
 * frente do que alguém classificou.
 */
function porEstante(leis: LeiDoCatalogo[]): { nome: string; leis: LeiDoCatalogo[] }[] {
  const prateleiras = AREAS_DA_LEI.map(({ area, nome }) => ({
    nome,
    leis: leis.filter((lei) => lei.areas.includes(area)),
  }));

  const semEstante = leis.filter((lei) => lei.areas.length === 0);
  if (semEstante.length > 0) prateleiras.push({ nome: nomeDaArea(null), leis: semEstante });

  return prateleiras.filter((prateleira) => prateleira.leis.length > 0);
}

/**
 * O título de uma estante: o nome, quantas leis, e o fio do módulo.
 *
 * O fio é o mesmo do cabeçalho da tela, mais fino - ele é o que faz a seção ler
 * como divisão do acervo, e não como mais um cartão com título.
 */
function TituloDaEstante({ nome, quantas }: { nome: string; quantas: number }) {
  return (
    <Flex vertical gap={6}>
      <Flex align="baseline" gap={8}>
        <Typography.Text
          strong
          style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          {nome}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {contar(quantas, 'lei', 'leis')}
        </Typography.Text>
      </Flex>

      <div
        aria-hidden
        style={{
          height: 1,
          borderRadius: 1,
          background: `linear-gradient(90deg, ${libraryAccent}, transparent 60%)`,
        }}
      />
    </Flex>
  );
}

/** As leis de uma prateleira, na forma que a pessoa escolheu ver o acervo. */
function Prateleira({
  leis,
  emLista,
  onAbrir,
}: {
  leis: LeiDoCatalogo[];
  emLista: boolean;
  onAbrir: (lei: LeiDoCatalogo) => void;
}) {
  if (emLista) {
    return (
      <Flex vertical>
        {leis.map((lei, indice) => (
          <LinhaDaLei
            key={lei.id}
            lei={lei}
            primeira={indice === 0}
            onAbrir={() => onAbrir(lei)}
          />
        ))}
      </Flex>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {leis.map((lei) => (
        <Col key={lei.id} xs={24} sm={12} lg={8}>
          <CartaoDaLei lei={lei} onAbrir={() => onAbrir(lei)} />
        </Col>
      ))}
    </Row>
  );
}

/**
 * A volta para onde a pessoa parou.
 *
 * Uma linha, e não um cartão: ela é atalho, não vitrine - se pesasse como os
 * cartões do acervo, competiria com eles pela primeira olhada, e o que ela
 * oferece já é conhecido de quem a vê.
 */
function ContinuarLendo({
  titulo,
  citacao,
  pagina,
  onAbrir,
}: {
  titulo: string;
  citacao: string;
  pagina: number;
  onAbrir: () => void;
}) {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  /*
   * **No celular a linha vira duas.** Lado a lado, os 390px sobravam para a
   * citação e faltavam para o nome: o convite lia "Continuar lendo C…", que é
   * exatamente a informação que ele existe para dar.
   */
  const emUmaLinha = telas.sm === true;

  return (
    <Flex
      vertical={!emUmaLinha}
      align={emUmaLinha ? 'center' : 'stretch'}
      justify="space-between"
      gap={emUmaLinha ? 12 : 2}
      role="button"
      tabIndex={0}
      onClick={onAbrir}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          onAbrir();
        }
      }}
      style={{
        padding: '10px 14px',
        borderRadius: token.borderRadius,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorFillQuaternary,
        cursor: 'pointer',
      }}
    >
      <Flex align="center" gap={10} style={{ minWidth: 0 }}>
        <BookOutlined style={{ color: libraryAccent, flexShrink: 0 }} />
        <Typography.Text ellipsis>
          Continuar lendo <strong>{titulo}</strong>
        </Typography.Text>
      </Flex>

      <Typography.Text
        type="secondary"
        style={{
          fontSize: 12,
          flexShrink: 0,
          // Alinhada ao texto do título, e não ao ícone, quando vira segunda linha.
          paddingInlineStart: emUmaLinha ? undefined : 26,
        }}
      >
        {citacao} · página {pagina}
      </Typography.Text>
    </Flex>
  );
}

/**
 * O que a busca achou **dentro** das leis.
 *
 * Some quando não há termo: é uma seção que só existe quando alguém procurou,
 * e um cabeçalho vazio embaixo do acervo seria mais uma linha para ignorar.
 */
function BuscaNoTexto({
  termo,
  achados,
  carregando,
  onAbrir,
}: {
  termo: string;
  achados?: AchadoNoTextoDaLei[];
  carregando: boolean;
  onAbrir: (achado: AchadoNoTextoDaLei) => void;
}) {
  const { token } = theme.useToken();

  if (termo.trim().length < 3) return null;

  return (
    <Flex vertical gap={8} style={{ width: '100%' }}>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}
      >
        No texto das leis
      </Typography.Text>

      {carregando && !achados ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (achados ?? []).length === 0 ? (
        <Typography.Text type="secondary">
          Nenhum dispositivo com “{termo.trim()}” nas leis publicadas.
        </Typography.Text>
      ) : (
        <Flex vertical>
          {achados!.map((achado, indice) => (
            <Flex
              key={`${achado.legislationId}:${achado.address}:${indice}`}
              vertical
              gap={2}
              role="button"
              tabIndex={0}
              onClick={() => onAbrir(achado)}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter' || evento.key === ' ') {
                  evento.preventDefault();
                  onAbrir(achado);
                }
              }}
              style={{
                paddingBlock: 10,
                borderTop: indice === 0 ? undefined : `1px solid ${token.colorSplit}`,
                cursor: 'pointer',
              }}
            >
              <Flex align="baseline" gap={8} wrap>
                <Typography.Text strong>{achado.address}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {achado.title}
                </Typography.Text>
              </Flex>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {achado.trecho}
              </Typography.Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

/* ------------------------------------------------------------- leitor */

function Leitor({
  leiId,
  onBloqueio,
}: {
  leiId: string;
  onBloqueio: (motivo: MotivoDeBloqueio, chave: string | null) => void;
}) {
  const { ler, filtrar, irParaPagina } = useEstadoDeLista();
  const telas = Grid.useBreakpoint();
  const { data: sessao } = useSession();
  const { message } = App.useApp();
  const clienteDeConsultas = useQueryClient();
  const [termo, setTermo] = useState('');
  const [sumarioAberto, setSumarioAberto] = useState(false);

  const pagina = ler.pagina();
  /*
   * O recorte fica na URL, e não em estado: quem manda o link de "os comentados
   * do Código Civil" quer que quem abre veja os comentados.
   */
  const recorte = ler.texto('recorte') as RecorteDaLeitura | undefined;

  const leitura = useQuery({
    queryKey: ['law', leiId, pagina, recorte],
    queryFn: () => api.readLaw(leiId, pagina, POR_PAGINA, undefined, recorte),
  });

  const totalDePaginas = Math.max(1, Math.ceil((leitura.data?.total ?? 0) / POR_PAGINA));
  /*
   * Memorizado porque o efeito do salto depende dele: sem isto o `?? []` daria
   * uma lista nova a cada render e o efeito rodaria em todos eles.
   */
  const artigos = useMemo(() => leitura.data?.itens ?? [], [leitura.data]);

  /*
   * **Onde a pessoa parou.** Efeito, e não escrita durante o render: o que
   * dispara a marca é a chegada da leitura, e gravar no `localStorage` é o
   * tipo de coisa que não pode acontecer duas vezes por render descartado.
   */
  const titulo = leitura.data?.title;
  useEffect(() => {
    if (titulo) guardarLeitura({ leiId, titulo, pagina });
  }, [leiId, titulo, pagina]);

  /*
   * **O sumário só é pedido quando o aluno abre.** São centenas de divisões no
   * Código Civil, e ninguém que veio ler o art. 2º precisa delas - carregá-las
   * junto com a lei seria peso na primeira tela pelo que a maioria não usa.
   */
  const sumario = useQuery({
    queryKey: ['law-sumario', leiId],
    queryFn: () => api.lawIndex(leiId, POR_PAGINA),
    enabled: sumarioAberto,
  });

  /**
   * Vai para o artigo, que é o que o campo sempre prometeu.
   *
   * **A resposta é semeada no cache antes de navegar.** O servidor já devolveu a
   * página inteira do artigo pedido; sem semear, trocar a URL faria a tela pedir
   * de novo exatamente o que acabou de chegar.
   */
  /*
   * O artigo que o salto ainda não achou na tela. É `ref` e não estado porque
   * nada é desenhado a partir dele: ele vive entre "o servidor disse a página"
   * e "a lista daquela página chegou ao DOM", e como estado só acrescentaria
   * um render a cada salto.
   */
  const alvoDoSalto = useRef<string | null>(null);

  const irParaArtigo = useMutation({
    mutationFn: (artigo: string) => api.readLaw(leiId, 1, POR_PAGINA, artigo),
    onSuccess: (dados, procurado) => {
      /*
       * **O salto sai do recorte, e tem de sair.** A página vem contada sobre a
       * lei inteira; com um filtro ligado, ela apontaria para outro lugar da
       * lista. Procurar um artigo é dizer "quero este", e o filtro é o que
       * estava no caminho.
       */
      clienteDeConsultas.setQueryData(['law', leiId, dados.pagina, undefined], dados);
      // Numa escrita só: `filtrar` e `irParaPagina` separados se atropelam, e o
      // recorte sobrevivia ao salto apontando para outro lugar da lista.
      /*
       * **Numa escrita só, e o `artigo` sai junto.** Ele é ordem de navegação,
       * não filtro: limpá-lo numa chamada à parte fazia as duas escritas
       * pegarem o mesmo instantâneo da URL e a última vencer - o parâmetro
       * sobrevivia ao salto e o disparava de novo na próxima troca de página.
       */
      irParaPagina(dados.pagina, { recorte: undefined, artigo: undefined });
      setTermo('');
      /*
       * **A página certa não é o artigo certo.** São vinte e cinco por página, e
       * o procurado pode ser o último: sem rolar até ele, quem pediu o art.
       * 1.241 cai numa tela que começa no 1.218 e parece que o salto errou.
       *
       * Se o artigo já estava nesta página, a âncora existe agora e o salto
       * acontece aqui. Se a página mudou, a lista nova ainda não chegou ao DOM
       * - o alvo fica guardado e quem rola é o efeito logo abaixo.
       */
      const alvo = dados.itens.find(
        (item) => soLetrasENumeros(item.article) === soLetrasENumeros(procurado),
      )?.article;
      alvoDoSalto.current = alvo && !rolarAteOArtigo(alvo) ? alvo : null;
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * **O artigo que veio da busca.** A tela do acervo manda `?artigo=` junto do
   * `?lei=`, e é aqui que ele vira página: quem sabe em que página um artigo
   * cai é o servidor, e é a mesma mutação do campo "Ir para o artigo" logo
   * acima. O parâmetro sai da URL no caminho, porque ele é uma ordem de
   * navegação e não um filtro - deixá-lo faria o salto se repetir a cada
   * mudança de página.
   */
  useEffect(() => {
    const alvo = alvoDoSalto.current;
    if (alvo && rolarAteOArtigo(alvo)) alvoDoSalto.current = null;
    // `artigos` na lista: é a chegada da página nova que põe a âncora no DOM.
  }, [artigos]);

  const artigoPedido = ler.texto('artigo');
  const saltar = irParaArtigo.mutate;
  useEffect(() => {
    if (artigoPedido) saltar(artigoPedido);
  }, [artigoPedido, saltar]);

  /*
   * O que o aluno escreveu vai inteiro para a API, que sabe a forma do rótulo:
   * `art. 1245`, `1.245` e `1245` são o mesmo artigo, e quem guarda o acervo é
   * quem tem como dizer isso (`library/ordem.ts`). A tela tinha a própria cópia
   * dessa leitura, e o admin não tinha nenhuma.
   */
  const procurar = () => {
    const artigo = termo.trim();
    if (artigo.length > 0) irParaArtigo.mutate(artigo);
  };

  const controles = (
    <Flex align="center" justify="space-between" gap={8}>
      <Button
        icon={<LeftOutlined />}
        disabled={pagina <= 1}
        onClick={() => irParaPagina(pagina - 1)}
      >
        {telas.sm ? 'Anteriores' : null}
      </Button>

      {/*
        * **O `1 de 84` virou campo, e no celular ele é a navegação.** Antes,
        * abaixo de `sm` sobravam duas setas nuas em volta de um número: para
        * chegar ao art. 1.245 do Código Civil, na página 51, eram cinquenta
        * toques em "Próximos".
        */}
      <Flex align="center" gap={6}>
        <InputNumber
          size="small"
          min={1}
          max={totalDePaginas}
          value={pagina}
          controls={false}
          style={{ width: 64 }}
          aria-label="Ir para a página"
          onPressEnter={(evento) => {
            const alvo = paginaPedida((evento.target as HTMLInputElement).value, totalDePaginas);
            if (alvo !== null) irParaPagina(alvo);
          }}
        />
        <Typography.Text type="secondary">de {totalDePaginas}</Typography.Text>
      </Flex>

      <Button
        icon={<RightOutlined />}
        iconPosition="end"
        disabled={pagina >= totalDePaginas}
        onClick={() => irParaPagina(pagina + 1)}
      >
        {telas.sm ? 'Próximos' : null}
      </Button>
    </Flex>
  );

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          style={{ padding: 0 }}
          onClick={() => filtrar({ lei: undefined, page: undefined })}
        >
          Legislação Comentada
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {leitura.data?.title ?? 'Carregando...'}
        </Typography.Title>
        {leitura.data ? (
          <Space size={8} wrap>
            <Tag color={libraryAccent}>{leitura.data.citation}</Tag>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {contar(leitura.data.artigos, 'artigo', 'artigos')} ·{' '}
              {contar(leitura.data.comentados, 'comentado', 'comentados', 'nenhum')}
            </Typography.Text>
          </Space>
        ) : null}
      </Space>

      {/*
        * **O campo e o botão são um controle só, e o sumário ocupa a linha
        * quando sobra.** Com os três soltos numa fileira que quebra, o celular
        * punha a busca apertada em cima e o "Sumário" sozinho embaixo, com um
        * vão à direita dele - o `Input.Search` cola o botão no campo, e o
        * `flex` faz o sumário ir de canto no desktop a largura inteira no
        * celular, em vez de órfão no meio.
        */}
      <Flex gap={8} wrap>
        <Input.Search
          allowClear
          value={termo}
          placeholder="Ir para o artigo (ex.: 20, 216-A, 1245)"
          enterButton="Ir"
          loading={irParaArtigo.isPending}
          style={{ flex: '1 1 240px' }}
          onChange={(evento) => setTermo(evento.target.value)}
          onSearch={procurar}
        />
        <Button
          icon={<UnorderedListOutlined />}
          style={{ flex: telas.sm ? '0 0 auto' : '1 1 100%' }}
          onClick={() => setSumarioAberto(true)}
        >
          Sumário
        </Button>
      </Flex>

      <Sumario
        aberto={sumarioAberto}
        onFechar={() => setSumarioAberto(false)}
        consulta={sumario}
        onEscolher={(paginaDaDivisao) => {
          irParaPagina(paginaDaDivisao);
          setSumarioAberto(false);
        }}
      />

      {/*
        * **O recorte é o que faz o comentário aparecer.** Há cinco comentários
        * publicados no produto inteiro, e quatro no Código Civil - espalhados
        * por 84 páginas. O `art. 108`, que é escritura pública e artigo de
        * primeira linha para cartório, está na página 5, e sem o filtro ninguém
        * chega nele.
        */}
      <Segmented
        block
        size={telas.sm ? 'middle' : 'small'}
        value={recorte ?? 'tudo'}
        options={[
          { label: 'A lei inteira', value: 'tudo' },
          {
            label: leitura.data ? `Comentados (${leitura.data.comentados})` : 'Comentados',
            value: 'comentados',
            disabled: leitura.data?.comentados === 0,
          },
          { label: 'Cobrados na prova', value: 'com-incidencia' },
        ]}
        onChange={(valor) =>
          filtrar({ recorte: valor === 'tudo' ? undefined : String(valor), page: undefined })
        }
      />

      {controles}

      {leitura.isPending ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : artigos.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            recorte === 'comentados'
              ? 'Nenhum artigo desta lei foi comentado ainda.'
              : recorte === 'com-incidencia'
                ? 'Nenhum artigo desta lei foi cobrado nas provas publicadas.'
                : 'Nenhum artigo nesta página.'
          }
        />
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {artigos.map((artigo) => (
            <Artigo
              key={artigo.article}
              /*
                A âncora do salto: quem chega pela busca cai na página certa, e
                é ela que leva o olho até a linha certa dentro da página - são
                vinte e cinco artigos, e o procurado pode ser o último.
              */
              id={ancoraDoArtigo(artigo.article)}
              artigo={artigo}
              leiId={leiId}
              citacao={leitura.data?.citation ?? ''}
              onBloqueio={onBloqueio}
            />
          ))}
        </Space>
      )}

      {controles}

      {/*
       * A identificação fica ao pé da leitura, e não numa caixa que se fecha: o
       * que dissuade é a pessoa saber que está identificada **enquanto** lê.
       */}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {AVISO}
        {sessao?.user ? ` Lendo como ${sessao.user.name} · ${sessao.user.email}.` : null}
      </Typography.Text>
    </Space>
  );
}

/* ------------------------------------------------------------- sumário */

/**
 * O sumário da lei, que é como se estuda um código.
 *
 * **Ninguém estuda "página 51 do Código Civil"; estuda "Direito das Coisas".**
 * A estrutura existia na fonte e vinha sendo jogada dentro do texto dos artigos;
 * agora que é dado, ela vira o menu que faltava - e o único caminho para chegar
 * ao art. 1.245 sem cinquenta toques em "Próximos".
 *
 * **Gaveta nos dois tamanhos, e não menu no desktop.** A árvore do Código Civil
 * tem centenas de nós: num `Popover` ela ou fica estreita demais para o título
 * caber ou cobre a lei que se está lendo. A gaveta dá altura inteira e fecha
 * sozinha ao escolher.
 */
function Sumario({
  aberto,
  onFechar,
  consulta,
  onEscolher,
}: {
  aberto: boolean;
  onFechar: () => void;
  consulta: UseQueryResult<DivisaoDaLei[], Error>;
  onEscolher: (pagina: number) => void;
}) {
  const telas = Grid.useBreakpoint();

  /*
   * **No celular o nó ocupa duas linhas, e não é preferência.** A árvore recua
   * 24px por nível, e numa tela de 340 sobravam 194px para rótulo, título e
   * página disputarem: `TÍTULO ÚNICO Das Diferentes Classes de Bens` quebrava no
   * meio da palavra - `Das Diferent` numa linha e `es Classes` na seguinte.
   *
   * Com o rótulo e a página numa linha e o título na outra, nada disputa
   * largura. No desktop a gaveta tem 480px e a linha única é mais densa, que é
   * o que se quer num menu de 397 entradas.
   */
  const paraOAntd = (nos: NoDaArvore[]): TreeDataNode[] =>
    nos.map((no) => ({
      key: no.chave,
      title: telas.md ? (
        <Flex align="baseline" gap={8} style={{ minWidth: 0 }}>
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
            {no.rotulo}
          </Typography.Text>
          {no.titulo ? <Typography.Text>{no.titulo}</Typography.Text> : null}
          <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            p. {no.pagina}
          </Typography.Text>
        </Flex>
      ) : (
        <Flex vertical style={{ minWidth: 0 }}>
          <Flex align="baseline" justify="space-between" gap={8}>
            <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
              {no.rotulo}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              p. {no.pagina}
            </Typography.Text>
          </Flex>
          {no.titulo ? (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {no.titulo}
            </Typography.Text>
          ) : null}
        </Flex>
      ),
      children: no.filhos.length > 0 ? paraOAntd(no.filhos) : undefined,
    }));

  const arvore = arvoreDeSecoes(consulta.data ?? []);

  return (
    <Drawer
      title="Sumário"
      placement={telas.md ? 'right' : 'bottom'}
      // `size` mede o eixo em que a gaveta cresce: 480px de largura à direita,
      // 80% de altura embaixo. `width` e `height` estão depreciadas no v6.
      size={telas.md ? 480 : '80%'}
      open={aberto}
      onClose={onFechar}
    >
      {consulta.isPending ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : consulta.error ? (
        <Alert type="error" showIcon title={consulta.error.message} />
      ) : arvore.length === 0 ? (
        /*
         * Lei curta não tem divisão nenhuma, e são dezenove das cinquenta e nove
         * do acervo. Dizer isso é melhor do que uma gaveta vazia.
         */
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Esta lei não se divide em livros, títulos ou capítulos."
        />
      ) : (
        <Tree
          blockNode
          defaultExpandedKeys={arvore.map((no) => no.chave)}
          treeData={paraOAntd(arvore)}
          onSelect={(_chaves, { node }) => {
            const escolhido = acharNo(arvore, String(node.key));
            if (escolhido) onEscolher(escolhido.pagina);
          }}
        />
      )}
    </Drawer>
  );
}

/** O nó da chave, em profundidade. A árvore é pequena e a busca é de um clique. */
function acharNo(nos: NoDaArvore[], chave: string): NoDaArvore | null {
  for (const no of nos) {
    if (no.chave === chave) return no;
    const achado = acharNo(no.filhos, chave);
    if (achado) return achado;
  }
  return null;
}

/* ------------------------------------------------------------- artigo */

/**
 * Só o que sobrevive à forma de escrever: `1.241`, `1241` e `art. 1241` são o
 * mesmo artigo, e o que a pessoa digita raramente é o rótulo que o acervo
 * guarda. Serve para achar, na página que voltou, a linha que ela pediu.
 */
function soLetrasENumeros(texto: string): string {
  return texto.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/** O id da âncora de um artigo na página. */
function ancoraDoArtigo(artigo: string): string {
  return `artigo-${artigo.replace(/[^\w-]/g, '')}`;
}

/**
 * Rola até o artigo, **se ele já estiver desenhado**. Devolve se conseguiu:
 * quem chama num momento em que a página ainda vai trocar precisa saber que
 * tem de tentar de novo quando a lista nova chegar.
 */
function rolarAteOArtigo(artigo: string): boolean {
  const elemento = document.getElementById(ancoraDoArtigo(artigo));
  if (!elemento) return false;
  elemento.scrollIntoView({ block: 'start' });
  return true;
}

function Artigo({
  artigo,
  id,
  leiId,
  citacao,
  onBloqueio,
}: {
  artigo: ArtigoParaLer;
  /** A âncora do salto, quando alguém chega procurando este artigo. */
  id?: string;
  leiId: string;
  /** `10.406/2002`, que é como o acervo e o filtro das questões nomeiam a lei. */
  citacao: string;
  onBloqueio: (motivo: MotivoDeBloqueio, chave: string | null) => void;
}) {
  /*
   * O comentário embutido é o de quem tem direito. Quem lê pela degustação abre
   * um a um, e é essa abertura que consome a cota - a rota avulsa existe para
   * isso, e é ela que devolve o motivo quando não há mais.
   */
  const [aberto, setAberto] = useState<string | null>(null);
  const { message } = App.useApp();

  const abrir = useMutation({
    mutationFn: () => api.readArticleCommentary(leiId, artigo.article),
    onSuccess: (resposta) => {
      setAberto(resposta.html);
      /*
       * No sucesso, e não no clique: o que interessa é o comentário que a pessoa
       * leu. Contar a tentativa que o cadeado recusou somaria a mesma pessoa nos
       * dois lados da conta - ela já é contada pelo `paywall_shown`.
       */
      registrarEvento({ nome: 'article_opened', lei: citacao, endereco: artigo.article });
    },
    onError: (erro: unknown) => {
      /*
       * O teto diário não é paywall: quem o encontra já comprou esta lei, e
       * abrir a oferta seria oferecer-lhe o que ele tem (D-50). Aviso curto, e
       * nada de modal.
       */
      if (ehTetoDiario(erro)) {
        message.info(AVISO_DO_TETO_DIARIO);
        return;
      }

      // A tela não decide acesso: o motivo que veio do backend escolhe a
      // mensagem do paywall, e a chave escolhe o produto (G-POLICY).
      onBloqueio(motivoDoBloqueio(erro) ?? 'not_entitled', chaveDoBloqueio(erro));
    },
  });

  const comentario = artigo.comentario ?? aberto;

  return (
    <Card
      id={id}
      size="small"
      /*
       * O artigo comentado se distingue **sem o aluno precisar abrir**: a borda
       * de acento e a etiqueta dizem onde há trabalho da casa.
       */
      style={artigo.temComentario ? { borderColor: libraryAccent } : undefined}
      title={
        <Space size={8} wrap>
          <Typography.Text strong>{comoSeCitaOArtigo(artigo.article)}</Typography.Text>
          {artigo.temComentario ? (
            <Tag color={libraryAccent} icon={<BookOutlined />} style={{ marginInlineEnd: 0 }}>
              Comentado
            </Tag>
          ) : null}
          {/*
           * A incidência é o argumento de ler este artigo antes do vizinho, e
           * sai do próprio catálogo: quantas questões publicadas o cobram.
           */}
          {/*
            * **A etiqueta leva às questões, e antes não levava a lugar nenhum.**
            * Ela é o melhor argumento para ler este artigo antes do vizinho - o
            * `art. 108` do Código Civil aparece em doze questões publicadas -, e
            * o aluno lia o número sem ter como ir atrás delas. Os ids já eram
            * conhecidos pelo `incidenciaPorArtigo`; faltava a porta.
            */}
          {artigo.incidencia > 0 ? (
            <Link
              to={`/estudar?module=enac&legislation=${encodeURIComponent(citacao)}&artigo=${encodeURIComponent(artigo.article)}`}
              onClick={(evento) => evento.stopPropagation()}
            >
              <Tag
                style={{ marginInlineEnd: 0, cursor: 'pointer' }}
                title={`Ver as questões que cobram este artigo`}
              >
                {contar(artigo.incidencia, 'questão', 'questões')} na prova
              </Tag>
            </Link>
          ) : null}
        </Space>
      }
    >
      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        {/*
          * **A anotação de vigência sai do corpo e vira etiqueta.** O compilado
          * imprime `(Redação dada pela Lei nº 13.146, de 2015) (Vigência)` dentro
          * do dispositivo, e no art. 4º do Código Civil ela se repete em cada
          * inciso, no mesmo peso do texto legal - cinco vezes num artigo de seis
          * linhas. O dado já vinha separado em `amendedBy` e a tela não o usava.
          *
          * **Sai da exibição, e não do acervo**: é o sinal de vigência legível
          * por máquina (D-25), e o que o verificador compara.
          */}
        {artigo.dispositivos.map((dispositivo) => (
          <div key={dispositivo.address}>
            <Typography.Paragraph style={{ margin: 0, maxWidth: MEDIDA_DE_LEITURA }}>
              {semAnotacao(dispositivo.content)}
            </Typography.Paragraph>
            {dispositivo.amendedBy ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Redação da {dispositivo.amendedBy}
              </Typography.Text>
            ) : null}
          </div>
        ))}

        {comentario ? (
          <Card
            size="small"
            type="inner"
            title="Comentário"
            styles={{ header: { borderColor: libraryAccent } }}
          >
            <div style={{ maxWidth: MEDIDA_DE_LEITURA }}>
            <ConteudoRico html={comentario} protegido />
          </div>
          </Card>
        ) : artigo.temComentario ? (
          <Button
            icon={<LockOutlined />}
            loading={abrir.isPending}
            onClick={() => abrir.mutate()}
            block
          >
            Ler o comentário deste artigo
          </Button>
        ) : null}
      </Space>
    </Card>
  );
}
